import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Avatar,
  Alert,
  Snackbar,
  Tab,
  Tabs,
  CircularProgress,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { parishService } from '../../services/parishService';
import {
  legalService,
  PLATFORM_SERVICE_CONTRACT_KEY,
  triggerBlobDownload,
  type SignedDocumentSummary,
} from '../../services/legalService';
import TermsOfService from '../../components/TermsOfService';
import { formatCepInput, lookupViaCep, onlyDigitsCep } from '../../utils/viaCep';

const PARISH_LOGO_MAX_KB = 300;
const PARISH_LOGO_MAX_BYTES = PARISH_LOGO_MAX_KB * 1024;

function formatFileSizeKb(bytes: number): string {
  const kb = Math.max(1, Math.round(bytes / 1024));
  return `${kb} KB`;
}

function parishLogoTooLargeMessage(fileSize: number): string {
  return (
    `Essa logo está um pouco pesada para nós 🙂 O arquivo tem ${formatFileSizeKb(fileSize)}, ` +
    `mas o limite é ${PARISH_LOGO_MAX_KB} KB. Tente uma versão menor — PNG ou JPG quadrado, ` +
    'com fundo transparente se possível.'
  );
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    const data = (error as { response?: { data?: { message?: string } } }).response?.data;
    if (data?.message) return data.message;
  }
  return error instanceof Error ? error.message : fallback;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProfilePage: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isParishAdmin = user?.userType === 'parish_admin';
  const parishTabIndex = 1;
  const extrasTabIndex = 2;
  const passwordTabIndex = isParishAdmin ? 3 : 1;
  const [tabValue, setTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [parishLoading, setParishLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [signedDocs, setSignedDocs] = useState<SignedDocumentSummary[]>([]);
  const [signedDocsError, setSignedDocsError] = useState<string | null>(null);
  const [showContractDialog, setShowContractDialog] = useState(false);
  const [signingContract, setSigningContract] = useState(false);

  // Dados do perfil do usuário
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Dados da paróquia (se for admin de paróquia)
  const [parishData, setParishData] = useState({
    name: user?.parish?.name || '',
    address: '',
    streetNumber: '',
    neighborhood: '',
    ibgeMunicipioCode: '',
    city: user?.parish?.city || '',
    state: user?.parish?.state || '',
    zipCode: '',
    phone: '',
    email: '',
    description: '',
    logoUrl: user?.parish?.logoUrl || '',
  });
  const [cepLoading, setCepLoading] = useState(false);

  useEffect(() => {
    if (!isParishAdmin) return;

    let cancelled = false;
    const loadParish = async () => {
      setParishLoading(true);
      try {
        const data = await parishService.getMyParishes();
        if (cancelled) return;
        const parish = data.parish;
        setParishData({
          name: parish.name || '',
          address: parish.address || '',
          streetNumber: parish.streetNumber || '',
          neighborhood: parish.neighborhood || '',
          ibgeMunicipioCode: parish.ibgeMunicipioCode || '',
          city: parish.city || '',
          state: parish.state || '',
          zipCode: parish.zipCode ? formatCepInput(parish.zipCode) : '',
          phone: parish.phone || '',
          email: parish.email || '',
          description: parish.description || '',
          logoUrl: parish.logoUrl || '',
        });
      } catch (error) {
        if (!cancelled) {
          setSnackbar({
            open: true,
            message: error instanceof Error ? error.message : 'Erro ao carregar dados da organização',
            severity: 'error',
          });
        }
      } finally {
        if (!cancelled) setParishLoading(false);
      }
    };

    loadParish();
    legalService
      .listMySignedDocuments()
      .then((docs) => {
        if (!cancelled) {
          setSignedDocs(docs);
          setSignedDocsError(null);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setSignedDocs([]);
          setSignedDocsError(extractApiErrorMessage(error, 'Não foi possível carregar os documentos assinados.'));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isParishAdmin]);

  const parishExtraData = {
    parishId: user?.parish?.id || user?.parishId || '—',
    cidadeEstado: user?.parish ? `${user.parish.city} / ${user.parish.state}` : '—',
    feePercentage: user?.parish?.feePercentage != null ? `${user.parish.feePercentage}%` : '—',
    tipoConta: user?.parish?.isMainParish ? 'Organização principal' : 'Unidade vinculada',
    saqueLiberado: user?.allowWithdrawalAccess ? 'Sim' : 'Não',
    onboardingPagarme: user?.parish?.pagarmeRecipientReady ? 'Pronto' : 'Pendente',
    possuiDadosSaque: user?.parish?.hasWithdrawalData ? 'Sim' : 'Não',
    permiteSaqueAntecipado: user?.parish?.features?.allowEarlyWithdrawalRequest ? 'Sim' : 'Não',
    permiteCriarSubgrupo: user?.parish?.features?.allowCreateSubgroup ? 'Sim' : 'Não',
    tipoUsuario: user?.userType || '—',
    emailAcesso: user?.email || '—',
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData({
      ...profileData,
      [name]: value
    });
  };
  
  const fillParishFromCep = async (rawCep: string) => {
    const digits = onlyDigitsCep(rawCep);
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const found = await lookupViaCep(digits);
      if (!found) {
        setSnackbar({
          open: true,
          message: 'CEP não encontrado. Preencha o endereço manualmente.',
          severity: 'error',
        });
        return;
      }
      setParishData((prev) => ({
        ...prev,
        zipCode: formatCepInput(found.cep),
        address: found.logradouro || prev.address,
        neighborhood: found.bairro || prev.neighborhood,
        city: found.localidade || prev.city,
        state: found.uf || prev.state,
        ibgeMunicipioCode: found.ibge || prev.ibgeMunicipioCode,
      }));
    } finally {
      setCepLoading(false);
    }
  };

  const handleParishChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let nextValue = value;
    if (name === 'zipCode') nextValue = formatCepInput(value);
    if (name === 'ibgeMunicipioCode') nextValue = value.replace(/\D/g, '').slice(0, 7);
    if (name === 'state') nextValue = value.toUpperCase().slice(0, 2);
    setParishData({
      ...parishData,
      [name]: nextValue,
    });
    if (name === 'zipCode' && onlyDigitsCep(nextValue).length === 8) {
      void fillParishFromCep(nextValue);
    }
  };
  
  const handleProfileSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Aqui seria feita a chamada à API para atualizar o perfil
    
    setSnackbar({
      open: true,
      message: 'Perfil atualizado com sucesso!',
      severity: 'success'
    });
  };
  
  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (profileData.newPassword !== profileData.confirmPassword) {
      setSnackbar({
        open: true,
        message: 'As senhas não coincidem',
        severity: 'error'
      });
      return;
    }
    
    // Aqui seria feita a chamada à API para atualizar a senha
    
    setSnackbar({
      open: true,
      message: 'Senha atualizada com sucesso!',
      severity: 'success'
    });
    
    // Limpar campos de senha
    setProfileData({
      ...profileData,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  };
  
  const handleParishSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!parishData.address.trim() || !parishData.streetNumber.trim() || !parishData.neighborhood.trim()) {
      setSnackbar({
        open: true,
        message: 'Preencha logradouro, número e bairro — necessários para emitir NFS-e.',
        severity: 'error',
      });
      return;
    }
    if (onlyDigitsCep(parishData.zipCode).length !== 8) {
      setSnackbar({
        open: true,
        message: 'Informe um CEP válido com 8 dígitos.',
        severity: 'error',
      });
      return;
    }
    try {
      const updated = await parishService.updateMyParish({
        ...parishData,
        zipCode: onlyDigitsCep(parishData.zipCode),
      });
      setParishData((prev) => ({
        ...prev,
        ...updated,
        zipCode: updated.zipCode ? formatCepInput(updated.zipCode) : prev.zipCode,
        logoUrl: updated.logoUrl || prev.logoUrl,
      }));
      setSnackbar({
        open: true,
        message: 'Dados da organização atualizados com sucesso!',
        severity: 'success',
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error instanceof Error ? error.message : 'Erro ao atualizar organização',
        severity: 'error',
      });
    }
  };

  const handleLogoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSnackbar({ open: true, message: 'Selecione um arquivo de imagem', severity: 'error' });
      return;
    }
    if (file.size > PARISH_LOGO_MAX_BYTES) {
      setSnackbar({ open: true, message: parishLogoTooLargeMessage(file.size), severity: 'error' });
      return;
    }

    setLogoUploading(true);
    try {
      const updated = await parishService.updateMyParishLogo(file);
      setParishData((prev) => ({ ...prev, logoUrl: updated.logoUrl || '' }));
      setSnackbar({ open: true, message: 'Logo atualizada com sucesso!', severity: 'success' });
    } catch (error) {
      setSnackbar({
        open: true,
        message: extractApiErrorMessage(error, 'Erro ao enviar logo'),
        severity: 'error',
      });
    } finally {
      setLogoUploading(false);
    }
  };

  const parishLogoSrc = parishData.logoUrl || undefined;
  const profileAvatarSrc = isParishAdmin ? parishLogoSrc : undefined;
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 8 }}>
      {isParishAdmin && (
        <input
          ref={logoInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleLogoFileChange}
        />
      )}
      <Paper elevation={3} sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="profile tabs"
          >
            <Tab label="Meu Perfil" />
            {isParishAdmin && (
              <Tab label="Dados da Organização" />
            )}
            {isParishAdmin && (
              <Tab label="Dados Extras" />
            )}
            <Tab label="Alterar Senha" />
          </Tabs>
        </Box>
        
        {/* Tab: Meu Perfil */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  bgcolor: 'primary.light',
                  '& img': { objectFit: 'contain', p: 1 },
                }}
                alt={isParishAdmin ? parishData.name || profileData.name : profileData.name}
                src={profileAvatarSrc}
              >
                {(isParishAdmin ? parishData.name : profileData.name)?.charAt(0) || 'U'}
              </Avatar>
              <Typography variant="h6" gutterBottom>
                {profileData.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {user?.userType === 'parish_admin' ? 'Administrador da organização' : 'Participante'}
              </Typography>
              {isParishAdmin && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 2 }}
                    disabled={logoUploading || parishLoading}
                    onClick={() => logoInputRef.current?.click()}
                  >
                    {logoUploading ? 'Enviando...' : 'Alterar logo'}
                  </Button>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', maxWidth: 220 }}>
                    Logo pequena na página do evento (Organizado por). Máx. {PARISH_LOGO_MAX_KB} KB — PNG ou JPG quadrado.
                  </Typography>
                </>
              )}
            </Grid>
            
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                Informações Pessoais
              </Typography>
              <Box component="form" onSubmit={handleProfileSubmit} noValidate>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Nome Completo"
                      name="name"
                      value={profileData.name}
                      onChange={handleProfileChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={profileData.email}
                      onChange={handleProfileChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                    >
                      Salvar Alterações
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </TabPanel>
        
        {/* Tab: Dados da Paróquia (apenas para admin de paróquia) */}
        {isParishAdmin && (
          <TabPanel value={tabValue} index={parishTabIndex}>
            <Typography variant="h6" gutterBottom>
              Informações da Organização
            </Typography>
            {parishLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
            <Box component="form" onSubmit={handleParishSubmit} noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Avatar
                      src={parishLogoSrc}
                      alt={parishData.name}
                      sx={{ width: 88, height: 88, '& img': { objectFit: 'contain', p: 0.75 } }}
                    >
                      {parishData.name?.charAt(0) || 'P'}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Logo da organização
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Logo leve para a página do evento (Organizado por). Máx. {PARISH_LOGO_MAX_KB} KB — ideal
                        quadrado, PNG ou JPG.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={logoUploading}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {logoUploading ? 'Enviando...' : parishLogoSrc ? 'Trocar logo' : 'Enviar logo'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    label="Nome da organização"
                    name="name"
                    value={parishData.name}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="CEP"
                    name="zipCode"
                    value={parishData.zipCode}
                    onChange={handleParishChange}
                    onBlur={() => void fillParishFromCep(parishData.zipCode)}
                    helperText={cepLoading ? 'Buscando CEP…' : 'Preenche logradouro, bairro, cidade e IBGE'}
                    InputProps={{
                      endAdornment: cepLoading ? <CircularProgress size={18} /> : undefined,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    required
                    fullWidth
                    label="Logradouro"
                    name="address"
                    value={parishData.address}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="Número"
                    name="streetNumber"
                    value={parishData.streetNumber}
                    onChange={handleParishChange}
                    placeholder="Ex.: 100 ou S/N"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    required
                    fullWidth
                    label="Bairro"
                    name="neighborhood"
                    value={parishData.neighborhood}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Cidade"
                    name="city"
                    value={parishData.city}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    required
                    fullWidth
                    label="Estado (UF)"
                    name="state"
                    value={parishData.state}
                    onChange={handleParishChange}
                    inputProps={{ maxLength: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="IBGE município"
                    name="ibgeMunicipioCode"
                    value={parishData.ibgeMunicipioCode}
                    onChange={handleParishChange}
                    helperText="Preenchido pelo CEP"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    name="phone"
                    value={parishData.phone}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email da organização"
                    name="email"
                    type="email"
                    value={parishData.email}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    name="description"
                    multiline
                    rows={4}
                    value={parishData.description}
                    onChange={handleParishChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                  >
                    Salvar Alterações
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ mt: 1 }}>
                    Documentos assinados
                  </Typography>
                  {signedDocsError ? (
                    <Typography variant="body2" color="error">
                      {signedDocsError}
                    </Typography>
                  ) : signedDocs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Esta conta ainda não tem contrato em PDF. Cadastros antigos só gravavam o aceite em checkbox.
                      Leia e assine o contrato vigente para gerar o arquivo.
                    </Typography>
                  ) : (
                    signedDocs.map((doc) => (
                      <Button
                        key={doc.id}
                        sx={{ mt: 1, mr: 1 }}
                        variant="outlined"
                        onClick={async () => {
                          try {
                            const blob = await legalService.downloadMySignedDocument(doc.id);
                            triggerBlobDownload(blob, `${doc.templateKey}.pdf`);
                          } catch {
                            setSnackbar({ open: true, message: 'Não foi possível baixar o documento.', severity: 'error' });
                          }
                        }}
                      >
                        {doc.title} ({new Date(doc.signedAt).toLocaleDateString('pt-BR')})
                      </Button>
                    ))
                  )}
                  {!signedDocs.some((d) => d.templateKey === PLATFORM_SERVICE_CONTRACT_KEY) && (
                    <Button
                      sx={{ mt: 2, display: 'block' }}
                      variant="contained"
                      disabled={signingContract}
                      onClick={() => setShowContractDialog(true)}
                    >
                      {signingContract ? 'Assinando...' : 'Ler e assinar contrato'}
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Box>
            )}
          </TabPanel>
        )}

        {/* Tab: Dados Extras (somente leitura para admin de paróquia) */}
        {isParishAdmin && (
          <TabPanel value={tabValue} index={extrasTabIndex}>
            <Typography variant="h6" gutterBottom>
              Dados extras da organização
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Estes dados são somente para consulta e não podem ser editados por esta tela.
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="ID da organização" value={parishExtraData.parishId} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Tipo de Conta" value={parishExtraData.tipoConta} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Cidade / Estado" value={parishExtraData.cidadeEstado} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Taxa da organização" value={parishExtraData.feePercentage} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Onboarding Pagar.me" value={parishExtraData.onboardingPagarme} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Possui Dados de Saque" value={parishExtraData.possuiDadosSaque} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Saque Liberado pelo Admin" value={parishExtraData.saqueLiberado} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Permite Saque Antecipado" value={parishExtraData.permiteSaqueAntecipado} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Permite criar setores" value={parishExtraData.permiteCriarSubgrupo} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Tipo de Usuário" value={parishExtraData.tipoUsuario} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="E-mail de Acesso" value={parishExtraData.emailAcesso} InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          </TabPanel>
        )}
        
        {/* Tab: Alterar Senha */}
        <TabPanel value={tabValue} index={passwordTabIndex}>
          <Typography variant="h6" gutterBottom>
            Alterar Senha
          </Typography>
          <Box component="form" onSubmit={handlePasswordSubmit} noValidate sx={{ maxWidth: 500 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Senha Atual"
                  name="currentPassword"
                  type="password"
                  value={profileData.currentPassword}
                  onChange={handleProfileChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Nova Senha"
                  name="newPassword"
                  type="password"
                  value={profileData.newPassword}
                  onChange={handleProfileChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  label="Confirmar Nova Senha"
                  name="confirmPassword"
                  type="password"
                  value={profileData.confirmPassword}
                  onChange={handleProfileChange}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Alterar Senha
                </Button>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Paper>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <TermsOfService
        open={showContractDialog}
        onClose={() => setShowContractDialog(false)}
        onAccept={async () => {
          try {
            setSigningContract(true);
            const template = await legalService.getTemplate(PLATFORM_SERVICE_CONTRACT_KEY);
            const result = await legalService.signMyPlatformContract({
              signatureName: profileData.name,
              contractViewed: true,
              legalAccepted: [{ key: template.key, version: template.version }],
            });
            setShowContractDialog(false);
            const docs = await legalService.listMySignedDocuments();
            setSignedDocs(docs);
            setSignedDocsError(null);
            setSnackbar({
              open: true,
              message: result.alreadySigned
                ? 'Este contrato já estava assinado.'
                : 'Contrato assinado. O PDF foi enviado por e-mail.',
              severity: 'success',
            });
            if (result.data?.id) {
              const blob = await legalService.downloadMySignedDocument(result.data.id);
              triggerBlobDownload(blob, 'contrato-assinado.pdf');
            }
          } catch (error) {
            setSnackbar({
              open: true,
              message: extractApiErrorMessage(error, 'Não foi possível assinar o contrato.'),
              severity: 'error',
            });
          } finally {
            setSigningContract(false);
          }
        }}
        contratanteName={parishData.name}
        contratanteAddress={[
          parishData.address,
          parishData.city && parishData.state ? `${parishData.city} - ${parishData.state}` : '',
        ]
          .filter(Boolean)
          .join(', ')}
        representativeName={profileData.name}
        representativeEmail={profileData.email || parishData.email}
      />
    </Container>
  );
};

export default ProfilePage;
